"use client";
import { useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import Image from "next/image";
import Icon from "@/components/icons";
import useTranslations from "@/i18n/useTranslations";
import { cn } from "@/lib/utils";
import testimonialIcon from "@/public/icons/testimonial-icon.svg";

export interface TestimonialCardData {
  content: string;
  image: string;
  name: string;
  productName: string;
}

interface TestimonialCardProps extends TestimonialCardData {
  /** Tailwind background class applied to the card surface (theme-aware). */
  cardBackgroundColor: string;
  /**
   * Total testimonials in the parent carousel. When there is only one
   * testimonial the card drops its min-height and `mt-auto` spacing so
   * it can size to its content instead of leaving empty space.
   */
  itemsCount?: number;
}

const COLLAPSED_QUOTE_HEIGHT = 126;
const EXPANDED_QUOTE_HEIGHT = 200;

export default function TestimonialCard({
  content,
  image,
  name,
  productName,
  cardBackgroundColor,
  itemsCount = 3,
}: TestimonialCardProps) {
  const isSingleItem = itemsCount === 1;
  const cardSizingClass = isSingleItem ? "" : "min-h-[200px] h-full";
  const userInfoSpacingClass = isSingleItem ? "" : "mt-auto";
  const t = useTranslations("ExpandableBlock");
  const contentRef = useRef<HTMLParagraphElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const handleToggle = () => {
    // When collapsing back to the truncated state, reset the inner scroll
    // position so the user lands at the top of the quote instead of staying
    // wherever they had scrolled in the expanded view.
    if (isExpanded && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
    setIsExpanded((prev) => !prev);
  };

  useEffect(() => {
    const measureOverflow = () => {
      const node = contentRef.current;
      if (!node) return;
      setIsOverflowing(node.scrollHeight > COLLAPSED_QUOTE_HEIGHT);
    };
    measureOverflow();
    const debounced = debounce(measureOverflow, 100);
    window.addEventListener("resize", debounced);
    return () => window.removeEventListener("resize", debounced);
  }, []);

  const quoteContainerClassName = cn(
    "flex-1 transition-max-height duration-500 ease-in-out relative",
    isExpanded
      ? "overflow-y-auto testimonial-quote-scroll pr-4"
      : "overflow-hidden",
  );

  const quoteContainerStyle = {
    maxHeight: `${
      isExpanded ? EXPANDED_QUOTE_HEIGHT : COLLAPSED_QUOTE_HEIGHT
    }px`,
  };

  return (
    <div
      className={`p-5 ${cardBackgroundColor} rounded-xl flex flex-col border border-lightgray-200 ${cardSizingClass} transition-all duration-300`}
    >
      <div className="flex flex-row gap-4">
        <Icon
          type="format-quote"
          svgProps={{
            width: 32,
            height: 32,
            className: "text-brand-500 scale-x-[-1] flex-shrink-0",
          }}
        />
        <div className="flex flex-col flex-1">
          <div
            ref={scrollContainerRef}
            className={quoteContainerClassName}
            style={quoteContainerStyle}
          >
            <p ref={contentRef} className="text-base text-gray-600 m-0">
              {content}
            </p>
            {!isExpanded && isOverflowing && (
              <div className="absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none" />
            )}
          </div>
          {isOverflowing && (
            <button
              type="button"
              onClick={handleToggle}
              className="text-primary-500 hover:text-primary-700 text-sm underline self-start mt-2"
            >
              {isExpanded ? t("showLess") : t("showMore")}
            </button>
          )}
        </div>
      </div>

      <div className={`flex flex-col gap-4 ${userInfoSpacingClass} pt-4`}>
        <div className="h-px w-full bg-putty-400" />
        <div className="flex flex-row items-center gap-4">
          <div className="h-[68px] w-[68px] rounded-full bg-primary-500 flex items-center justify-center overflow-hidden">
            {image ? (
              <Image
                src={image}
                alt={name}
                width={68}
                height={68}
                className="rounded-full object-cover w-full h-full"
              />
            ) : (
              <Image
                src={testimonialIcon}
                height={67}
                width={67}
                alt=""
                className="m-0"
              />
            )}
          </div>
          <div className="flex flex-col text-sm font-bold">
            <div>{name}</div>
            <div>{productName}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
